import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { BRAND } from '@/lib/brand';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Lock, ArrowLeft, Check } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { toast.error('Minimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Contrasena actualizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Token invalido o expirado');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0B0C10' }}>
        <Card className="bg-card/50 backdrop-blur-md border-white/5 max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Lock className="w-10 h-10 mx-auto text-destructive mb-4" />
            <p className="text-white font-medium mb-2">Token no encontrado</p>
            <p className="text-muted-foreground text-sm mb-4">Solicita un nuevo enlace de restablecimiento.</p>
            <Link to="/login"><Button variant="outline" className="border-white/10" data-testid="back-to-login-btn"><ArrowLeft className="w-4 h-4 mr-2" />Volver al Login</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0B0C10' }}>
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-10">
          <img src={BRAND.logoWhite} alt="LEADCEM" className="h-12 mx-auto object-contain mb-2" />
        </div>
        <Card className="bg-card/50 backdrop-blur-md border-white/5">
          <CardHeader className="pb-2 text-center">
            <Lock className="w-8 h-8 mx-auto text-primary mb-2" />
            <h2 className="font-heading text-lg font-semibold text-white">{done ? 'Contrasena Actualizada' : 'Nueva Contrasena'}</h2>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-4"><Check className="w-6 h-6 text-emerald-400" /></div>
                <p className="text-muted-foreground text-sm mb-4">Tu contrasena ha sido actualizada exitosamente.</p>
                <Button onClick={() => navigate('/login')} className="bg-primary hover:bg-primary/90 font-semibold" data-testid="go-to-login-btn">Iniciar Sesion</Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nueva contrasena</Label>
                  <Input data-testid="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 caracteres" required className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label>Confirmar contrasena</Label>
                  <Input data-testid="confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repetir contrasena" required className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="reset-password-btn" disabled={loading}>
                  {loading ? 'Actualizando...' : 'Restablecer Contrasena'}
                </Button>
                <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-primary" style={{ transition: 'color 0.2s ease' }}>Volver al Login</Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
